import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { categorySelectableProducts } from '@shared/helpers/category-selectable-products.helpers';
import { Recipe } from '@shared/models/recipe.model';
import { MappingReferenceType } from '@shared/models/element-import.model';
import { TypeaheadField, TypeaheadOption } from '@ui/TypeaheadField';
import { Icon, NodonIcons } from '@ui/Icon/Icon';
import {
  getElementIconCategoryName,
  getProductCategoryIconName,
} from '@features/elements/helpers/get-element-category-icon-name.helper';

/** Picker option tagged with the nodon entity it maps to (product or recipe). */
type MappingOption = TypeaheadOption & {
  type: MappingReferenceType;
  iconName: keyof NodonIcons['elementCategory'];
};

/**
 * In-row mapping picker for csv-import. Thin wrapper around the generic
 * {@link TypeaheadField}: same input look and behaviour as the element list's
 * name field, plus a typeahead dropdown over the products selectable through the
 * material product categories ({@link categorySelectableProducts}) and the org's
 * recipes (DEV-3387). Recipes and products both carry category icons;
 * {@link onPick} reports which kind was chosen so the caller writes the right
 * `referenceType` to the draft.
 */
export const MappingPicker: React.FC<{
  selectedKey: string | undefined;
  recipes: Recipe[];
  placeholder: string;
  searchPlaceholder?: string;
  paddingRight?: number | string;
  /** `id === undefined` clears the mapping (field cleared + Enter). */
  onPick: (id: string | undefined, type: MappingReferenceType) => void;
  disabled?: boolean;
  /**
   * When the field already has a mapping, the first click only selects the
   * import row; a second click (row selected) opens the dropdown.
   */
  isRowSelected?: boolean;
  shrinkWhenIdle?: boolean;
  onEmptyActivate?: (ctx: { isRowSelected: boolean | undefined }) => boolean;
  onEditingChange?: (isEditing: boolean) => void;
}> = ({
  selectedKey,
  recipes,
  placeholder,
  searchPlaceholder,
  paddingRight,
  onPick,
  disabled,
  isRowSelected,
  shrinkWhenIdle,
  onEmptyActivate,
  onEditingChange,
}) => {
  const { classes } = useStyles();

  const items = useMemo<MappingOption[]>(
    () => [
      ...categorySelectableProducts.map((p) => ({
        key: p.id,
        label: p.name,
        type: 'product' as const,
        iconName: getProductCategoryIconName(p.id),
      })),
      ...recipes.map((r) => ({
        key: r.id,
        label: r.name,
        type: 'recipe' as const,
        iconName: getElementIconCategoryName(r.category_id),
      })),
    ],
    [recipes],
  );

  const handlePick = (key: string | undefined): void => {
    // On unset (key undefined) the type is irrelevant — the caller keys off the
    // undefined id — so the fallback 'product' is just a placeholder.
    const type = items.find((i) => i.key === key)?.type ?? 'product';
    onPick(key, type);
  };

  return (
    <TypeaheadField
      items={items}
      selectedKey={selectedKey}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      paddingRight={paddingRight}
      onPick={handlePick}
      disabled={disabled}
      isRowSelected={isRowSelected}
      shrinkWhenIdle={shrinkWhenIdle}
      onEmptyActivate={onEmptyActivate}
      onEditingChange={onEditingChange}
      renderItem={(item) => (
        <span className={classes.optionRow}>
          {/* Medium icons + body2 — same density as element-list Menu.Item. */}
          <span className={classes.optionIcon}>
            <Icon category="elementCategory" icon={item.iconName} />
          </span>
          <Typography variant="body2" component="span" noWrap>
            {item.label}
          </Typography>
        </span>
      )}
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  optionRow: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  optionIcon: {
    alignItems: 'center',
    color: theme.palette.text.subtle,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    minWidth: 20,
    width: 20,
  },
}));
